
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { getTableName } from '@/worker/db/sqlHelpers';
import gdbx from '@/store/modules/gdbx';
import * as echarts from 'echarts';
import { GobiColor } from '@/helpers/color';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import SwitchAmountQuantityToggle from '@/components/SwitchAmountQuantityToggle.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		StackedBarChartFilterable,
		IncludeTaxToggle,
		SelectFilter,
		SwitchAmountQuantityToggle,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
	},
})
export default class StockGroupSalesPurchaseBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['stocks', 'sales', 'purchases'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'defaultStockGroup'
		| 'locations'
	> = ['dateRange', 'stockItems'];

	// public legends = ['Total Sales', 'Cost', 'Profit'];
	public switchAmountQuantity: string = 'amount';

	public xAxisData: string[] = [];
	public series: echarts.ECharts[] | any = [];

	public triggerSwitchAmountQuantity(value) {
		this.switchAmountQuantity = value;
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get allStockGroup() {
		return Object.keys(gdbx.stockGroupNames);
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedStockItems,
			switchAmountQuantity,
			includeCreditNote,
			includeDebitNote,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedStockItems,
			switchAmountQuantity,
			includeCreditNote,
			includeDebitNote,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const queryHead = await this._ensureTablesLoaded(
			['stockSales', 'stockPurchase', 'globalStocks'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		const dateFilter = `TRY_CAST("POSTDATE" AS BIGINT) >= ${this.selectedDateRange[0]} AND TRY_CAST("POSTDATE" AS BIGINT) <= ${this.selectedDateRange[1]}`;

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		let stockItemFilter = '';
		if (this.selectedStockItems && this.selectedStockItems.length > 0) {
			const list = this.selectedStockItems.map((s: string) => `'${escapeStr(s)}'`).join(', ');
			stockItemFilter = ` AND "ITEMCODE" IN (${list})`;
		}

		const isAmount = this.switchAmountQuantity === 'amount';

		// Sales doc types
		const salesDocTypes = ['IV', 'DO', 'CS'];
		if (this.includeDebitNote) { salesDocTypes.push('DN'); }
		if (this.includeCreditNote) { salesDocTypes.push('CN'); }
		const salesDocFilter = ` AND "DOCTYPE" IN (${salesDocTypes.map((d) => `'${d}'`).join(', ')})`;

		// Purchase doc types
		const purchaseDocTypes = ['PI', 'GR', 'CP'];
		if (this.includeDebitNote) { purchaseDocTypes.push('SD'); }
		if (this.includeCreditNote) { purchaseDocTypes.push('SC'); }
		const purchaseDocFilter = ` AND "DOCTYPE" IN (${purchaseDocTypes.map((d) => `'${d}'`).join(', ')})`;

		// Sales value: negate because sales qty is negative
		const salesValueExpr = isAmount
			? 'SUM(-(TRY_CAST("PRICE" AS DOUBLE) * TRY_CAST("QTY" AS DOUBLE)))'
			: 'SUM(-TRY_CAST("QTY" AS DOUBLE))';

		// Purchase value: positive direction
		const purchaseValueExpr = isAmount
			? 'SUM(TRY_CAST("COST" AS DOUBLE) * TRY_CAST("QTY" AS DOUBLE))'
			: 'SUM(TRY_CAST("QTY" AS DOUBLE))';

		const [salesRows, purchaseRows, stockRows] = await Promise.all([
			queryHead.rawQuery(`
				SELECT "STOCKGROUP", ${salesValueExpr} as val
				FROM ${getTableName('stockSales')}
				WHERE ${dateFilter}${salesDocFilter}${stockItemFilter}
				GROUP BY "STOCKGROUP"
			`),
			queryHead.rawQuery(`
				SELECT "STOCKGROUP", ${purchaseValueExpr} as val
				FROM ${getTableName('stockPurchase')}
				WHERE ${dateFilter}${purchaseDocFilter}${stockItemFilter}
				GROUP BY "STOCKGROUP"
			`),
			queryHead.rawQuery(`
				SELECT "STOCKGROUP", "STOCKGROUPDESC"
				FROM ${getTableName('globalStocks')}
				WHERE "STOCKGROUP" IS NOT NULL
				GROUP BY "STOCKGROUP", "STOCKGROUPDESC"
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.9, 1);

		// Build stock group desc map
		const stockGroupDescMap: { [key: string]: string } = {};
		for (const row of stockRows) {
			if (row.STOCKGROUP) {
				stockGroupDescMap[row.STOCKGROUP] = row.STOCKGROUPDESC || '';
			}
		}

		// Build sales/purchase maps by stock group
		const salesMap = new Map<string, number>();
		for (const row of salesRows) {
			salesMap.set(row.STOCKGROUP || '', Number(row.val) || 0);
		}
		const purchaseMap = new Map<string, number>();
		for (const row of purchaseRows) {
			purchaseMap.set(row.STOCKGROUP || '', Number(row.val) || 0);
		}

		// Get all unique stock groups
		const allStockGroups = [
			...new Set([...salesMap.keys(), ...purchaseMap.keys()]),
		].sort();

		const xAxisLabels: string[] = [];
		const salesData: number[] = [];
		const purchaseData: number[] = [];

		for (const stockGroup of allStockGroups) {
			const groupLabel = stockGroupDescMap[stockGroup]
				? `[${stockGroup}] ${stockGroupDescMap[stockGroup]}`
				: stockGroup;
			xAxisLabels.push(groupLabel);
			salesData.push(salesMap.get(stockGroup) || 0);
			purchaseData.push(purchaseMap.get(stockGroup) || 0);
		}

		this.xAxisData = xAxisLabels;

		const salesResult = {
			name: 'Total Sales',
			type: 'bar',
			barWidth: '30%',
			color: GobiColor.COLOR1,
			data: salesData,
		};

		const purchaseResult = {
			name: 'Total Purchase',
			type: 'bar',
			barWidth: '30%',
			color: GobiColor.COLOR3,
			data: purchaseData,
		};

		this.series = [salesResult, purchaseResult];

		this.saveHistory('series', 'xAxisData');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
